/**
 * shared/phrase.js - did the child say it?
 *
 * PLATFORM, not one game's. It was written inside Bubble Pop and moved here
 * the moment a second game needed it, which took about ten minutes - see
 * docs/audit/GAME-02-CONTRACT.md. Nothing in it knows about bubbles or doors.
 *
 * PURE. No React, no DOM, no clock. Every rule about what counts as having
 * said something lives here, and it is built on the engine's matcher rather
 * than beside it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS AT ALL
 *
 * The engine already has judgement.targetProduced(), which applies the phase's
 * strictness band (section 8.3) and searches the alternatives. Two things it
 * does not do, both of which any real game needs:
 *
 * 1. It takes ONE target string. A correct production comes back from the
 *    recogniser as any of several strings - "sun" as "son", "flower" as
 *    "flour" - so a game has to call it once per accepted variant. See
 *    content/objects.js for why that list exists.
 *
 * 2. It is order-free, deliberately: word order is Phase 5 component D's box.
 *    Phase 3 component C ("Combination: 'want apple' not 'apple want'") does
 *    need order, over a two-token phrase, so that is computed here from the
 *    engine's own tokeniser.
 *
 * Both are noted in docs/audit/GAME-01.md section 7 as things the engine could
 * absorb; until it does, every game will write this file, and they should all
 * write the same one.
 */

import { targetProduced, tokenise } from '../../../engine/judgement.js';
import { getStrictness } from '../../../engine/framework.js';

/**
 * The transcripts worth searching: the primary result, plus any alternative
 * the phase's confidence floor does not exclude.
 *
 * An alternative with no confidence is KEPT. Firefox and some Safari builds
 * never score them, and discarding those would make the whole alternatives
 * rule Chrome-only - the same reasoning as ENGINE-BUILD.md section 2.5.
 *
 * @param {{phase:number, transcript?:string|null,
 *          alternatives?:Array<{transcript:string, confidence:number|null}>}} input
 * @returns {string[]} candidate strings, primary first
 */
export function candidates({ phase, transcript, alternatives = [] }) {
  const floor = getStrictness(phase)?.rules.minConfidence ?? 0;
  const out = [];
  if (transcript) out.push(transcript);
  for (const alt of alternatives) {
    if (!alt || typeof alt.transcript !== 'string' || !alt.transcript) continue;
    if (alt.confidence !== null && alt.confidence !== undefined && alt.confidence < floor) continue;
    if (!out.includes(alt.transcript)) out.push(alt.transcript);
  }
  return out;
}

/**
 * Was any of these phrases produced?
 *
 * @param {object} input
 * @param {number} input.phase        selects the strictness band
 * @param {string[]} input.targets    accepted phrases, most canonical first
 * @param {string|null} [input.transcript]
 * @param {Array} [input.alternatives]
 * @returns {{produced:boolean, target:string|null, matched:string|null,
 *            matchedIn:'transcript'|'alternative'|null, rule:string|null,
 *            reason:string}}
 */
export function producedAny({ phase, targets = [], transcript, alternatives = [] }) {
  let lastReason = 'Nothing was recognised.';

  for (const target of targets) {
    const hit = targetProduced({ phase, target, transcript, alternatives });
    if (hit.produced) {
      return {
        produced: true,
        target,
        matched: hit.matched,
        matchedIn: hit.matchedIn,
        rule: hit.rule,
        reason: hit.reason,
      };
    }
    lastReason = hit.reason;
  }

  return {
    produced: false,
    target: targets[0] || null,
    matched: transcript || null,
    matchedIn: null,
    rule: null,
    // The reason names the canonical target, not the last variant tried, so a
    // therapist reading the log sees "apple", not "apples".
    reason: targets.length
      ? (transcript
          ? `Expected "${targets[0]}"; heard "${transcript}".`
          : `Expected "${targets[0]}"; nothing was recognised.`)
      : lastReason,
  };
}

/**
 * Phase 3 component C: both words present, in the right order, in the SAME
 * candidate.
 *
 * Same candidate matters. "want" in the primary transcript and "apple" in an
 * alternative is two hypotheses about one utterance, not evidence that the
 * child said both.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {string[]} input.carrierWords accepted carrier tokens
 * @param {string[]} input.nounWords    accepted noun tokens
 * @param {string|null} [input.transcript]
 * @param {Array} [input.alternatives]
 * @returns {{carrier:boolean, noun:boolean, ordered:boolean,
 *            candidate:string|null, carrierWord:string|null,
 *            nounWord:string|null, reason:string}}
 */
export function orderedCombination({ phase, carrierWords, nounWords, transcript, alternatives = [] }) {
  const pool = candidates({ phase, transcript, alternatives });

  let sawCarrier = null;
  let sawNoun = null;

  for (const candidate of pool) {
    const tokens = tokenise(candidate);
    const ci = tokens.findIndex((t) => carrierWords.includes(t));
    const ni = tokens.findIndex((t) => nounWords.includes(t));

    if (ci !== -1 && sawCarrier === null) sawCarrier = tokens[ci];
    if (ni !== -1 && sawNoun === null) sawNoun = tokens[ni];

    if (ci !== -1 && ni !== -1 && ci < ni) {
      return {
        carrier: true,
        noun: true,
        ordered: true,
        candidate,
        carrierWord: tokens[ci],
        nounWord: tokens[ni],
        reason: `"${tokens[ci]}" before "${tokens[ni]}" in "${candidate}".`,
      };
    }
  }

  // Both words somewhere, but never correctly ordered in one hypothesis.
  const both = sawCarrier !== null && sawNoun !== null;
  return {
    carrier: sawCarrier !== null,
    noun: sawNoun !== null,
    ordered: false,
    candidate: pool[0] || null,
    carrierWord: sawCarrier,
    nounWord: sawNoun,
    reason: both
      ? `Both words heard, but not in order (${sawNoun} before ${sawCarrier}).`
      : (sawCarrier !== null
          ? `Heard the action word "${sawCarrier}" with no object after it.`
          : (sawNoun !== null
              ? `Heard "${sawNoun}" on its own, with no action word in front.`
              : 'Neither word was heard.')),
  };
}

export default { candidates, producedAny, orderedCombination };
